import React, { useEffect, useRef, useState } from "react";
import { Lock, Upload, Trash2, Check, Loader2, ImagePlus, LogOut } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useSiteImages, refreshSiteImages } from "@/lib/siteImages";
import { SERVICES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "styne_admin_token";

const SLOTS = [
  { slot: "hero", label: "Home page banner", hint: "Your best finished job, landscape", fallback: SERVICES.bathrooms.image },
  { slot: "service-bathrooms", label: "Bathrooms", hint: "Finished renovation", fallback: SERVICES.bathrooms.image },
  { slot: "service-civil-drainage", label: "Civil & Drainage", hint: "Trench, pipes, excavator", fallback: SERVICES["civil-drainage"].image },
  { slot: "service-maintenance", label: "Maintenance", hint: "On the tools — tap, toilet, gas", fallback: SERVICES.maintenance.image },
  { slot: "service-emergency-plumbing", label: "Emergency Plumbing", hint: "Burst pipe fix or van at night", fallback: SERVICES["emergency-plumbing"].image },
  { slot: "service-blocked-drains", label: "Blocked Drains", hint: "CCTV camera or jetter", fallback: SERVICES["blocked-drains"].image },
  { slot: "service-hot-water", label: "Hot Water", hint: "New system installed", fallback: SERVICES["hot-water"].image },
  { slot: "areas", label: "Service Areas page", hint: "Van on a local street / job with hills", fallback: SERVICES.areas.image },
  { slot: "about-portrait", label: "About — photo of you", hint: "You on site in work gear (portrait OK)" },
  { slot: "about-van", label: "About — your van / ute", hint: "Branding showing" },
  { slot: "contact", label: "Contact page", hint: "You or the van (optional)" },
];

// Shrinks big phone photos in the browser so uploads are fast and don't crash mobile
const compress = async (file) => {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2400 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    return blob ? new File([blob], "photo.jpg", { type: "image/jpeg" }) : file;
  } catch {
    return file;
  }
};

const uploadOne = async (token, slot, file, meta = {}) => {
  const fd = new FormData();
  fd.append("slot", slot);
  fd.append("file", await compress(file));
  Object.entries(meta).forEach(([k, v]) => fd.append(k, v || ""));
  const r = await fetch(`${API}/admin/site-images`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
    err.status = r.status;
    throw err;
  }
  return data;
};

const PinGate = ({ onToken }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await fetch(`${API}/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pin }),
    }).catch(() => null);
    const data = r ? await r.json().catch(() => ({})) : {};
    setBusy(false);
    if (r?.ok && data.token) {
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onToken(data.token);
    } else setError(typeof data.detail === "string" ? data.detail : "Couldn't connect — try again");
  };
  return (
    <form onSubmit={submit} className="max-w-sm mx-auto border border-line bg-elevated p-10" data-testid="upload-pin-form">
      <Lock size={28} className="text-gold mb-6" />
      <h1 className="font-display text-4xl text-ink mb-2">Photo Upload</h1>
      <p className="text-muted mb-8">Enter your PIN to manage the website photos.</p>
      <input
        type="password"
        inputMode="numeric"
        autoFocus
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="w-full bg-bg border border-line focus:border-gold outline-none px-4 py-4 text-ink text-2xl tracking-[0.5em] text-center"
        placeholder="••••"
        data-testid="upload-pin-input"
      />
      {error && <p className="text-red-400 text-sm mt-3" data-testid="upload-pin-error">{error}</p>}
      <button type="submit" disabled={busy || pin.length < 4} className="btn-gold w-full mt-6 justify-center disabled:opacity-50" data-testid="upload-pin-submit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : "Unlock"}
      </button>
    </form>
  );
};

const SlotRow = ({ def, current, busy, onPick, onRemove }) => {
  const inputRef = useRef(null);
  const src = current || def.fallback;
  return (
    <div className="flex items-center gap-4 border border-line bg-elevated p-4" data-testid={`upload-slot-${def.slot}`}>
      <div className="w-24 h-20 shrink-0 bg-bg overflow-hidden flex items-center justify-center">
        {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={20} className="text-dim" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-ink font-display text-lg leading-tight">{def.label}</div>
        <div className="text-muted text-xs">{def.hint}</div>
        <div className={`text-[10px] tracking-widerx uppercase mt-1 ${current ? "text-gold" : "text-dim"}`} data-testid={`upload-slot-status-${def.slot}`}>
          {current ? "Your photo" : def.fallback ? "Using placeholder" : "Empty — hidden on site"}
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} data-testid={`upload-slot-input-${def.slot}`} />
      <div className="flex flex-col gap-2 shrink-0">
        <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-gold !px-4 !py-2 text-xs" data-testid={`upload-slot-btn-${def.slot}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {current ? "Replace" : "Upload"}
        </button>
        {current && (
          <button onClick={onRemove} disabled={busy} className="text-muted hover:text-red-400 text-xs inline-flex items-center gap-1 justify-center" data-testid={`upload-slot-remove-${def.slot}`}>
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>
    </div>
  );
};

const GalleryUploader = ({ busy, onUpload }) => {
  const inputRef = useRef(null);
  const [meta, setMeta] = useState({ title: "", suburb: "", category: "", scope: "" });
  const field = (k, placeholder) => (
    <input
      value={meta[k]}
      onChange={(e) => setMeta({ ...meta, [k]: e.target.value })}
      placeholder={placeholder}
      className="bg-bg border border-line focus:border-gold outline-none px-3 py-3 text-ink text-sm w-full"
      data-testid={`gallery-meta-${k}`}
    />
  );
  return (
    <div className="border border-gold/40 bg-elevated p-5 space-y-3" data-testid="gallery-uploader">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {field("title", "Job name (e.g. Ensuite reno)")}
        {field("suburb", "Suburb")}
        <select value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} className="bg-bg border border-line px-3 py-3 text-ink text-sm" data-testid="gallery-meta-category">
          <option value="">Category…</option>
          {["Bathrooms", "Civil & Drainage", "Maintenance", "Emergency", "Blocked Drains", "Hot Water"].map((c) => <option key={c}>{c}</option>)}
        </select>
        {field("scope", "What was done (optional)")}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onUpload(Array.from(e.target.files || []), meta); e.target.value = ""; }} data-testid="gallery-upload-input" />
      <button onClick={() => inputRef.current?.click()} disabled={busy} className="btn-gold w-full justify-center" data-testid="gallery-upload-btn">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Choose gallery photos
      </button>
      <p className="text-dim text-xs">Pick one or many — they all get these details. Fill in, upload, then change the details for the next job.</p>
    </div>
  );
};

export default function PhotoUpload() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [busySlot, setBusySlot] = useState(null);
  const [progress, setProgress] = useState("");
  const { slots, gallery } = useSiteImages();

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const handleError = (e) => {
    if (e.status === 401) { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }
    toast.error(e.message);
  };

  const pickSlot = async (slot, file) => {
    if (!file) return;
    setBusySlot(slot);
    try {
      await uploadOne(token, slot, file);
      await refreshSiteImages();
      toast.success("Photo is live on the site");
    } catch (e) { handleError(e); }
    setBusySlot(null);
  };

  const remove = async (id) => {
    const r = await fetch(`${API}/admin/site-images/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { await refreshSiteImages(); toast.success("Removed"); } else handleError({ status: r.status, message: "Couldn't remove" });
  };

  const uploadGallery = async (files, meta) => {
    if (!files.length) return;
    setBusySlot("gallery");
    let done = 0;
    for (const f of files) {
      setProgress(`Uploading ${done + 1} of ${files.length}…`);
      try { await uploadOne(token, "gallery", f, meta); done += 1; } catch (e) { handleError(e); if (e.status === 401) break; }
    }
    setProgress("");
    await refreshSiteImages();
    if (done) toast.success(`${done} photo${done > 1 ? "s" : ""} added to the gallery`);
    setBusySlot(null);
  };

  const slotId = (slot) => slots[slot]?.split("/").pop();

  if (!token) return <div className="py-24 px-6" data-testid="photo-upload-page"><PinGate onToken={setToken} /></div>;

  return (
    <div className="mx-auto max-w-3xl px-5 py-16" data-testid="photo-upload-page">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="flex items-start justify-between gap-4 mb-10">
        <div>
          <div className="eyebrow mb-3">Owner only</div>
          <h1 className="font-display text-4xl md:text-5xl text-ink">Website Photos</h1>
          <p className="text-muted mt-2">Tap Upload next to any spot. Photos go live straight away.</p>
        </div>
        <button onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }} className="text-muted hover:text-gold text-xs inline-flex items-center gap-1 mt-2" data-testid="upload-logout">
          <LogOut size={14} /> Lock
        </button>
      </div>

      <h2 className="font-display text-2xl text-ink mb-4">Page photos</h2>
      <div className="space-y-3 mb-16">
        {SLOTS.map((d) => (
          <SlotRow
            key={d.slot}
            def={d}
            current={slots[d.slot]}
            busy={busySlot === d.slot}
            onPick={(f) => pickSlot(d.slot, f)}
            onRemove={() => remove(slotId(d.slot))}
          />
        ))}
      </div>

      <h2 className="font-display text-2xl text-ink mb-4">Gallery — your jobs</h2>
      <GalleryUploader busy={busySlot === "gallery"} onUpload={uploadGallery} />
      {progress && <p className="text-gold text-sm mt-3 inline-flex items-center gap-2" data-testid="gallery-upload-progress"><Loader2 size={14} className="animate-spin" /> {progress}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6" data-testid="gallery-uploaded-list">
        {gallery.map((g) => (
          <div key={g.id} className="relative border border-line bg-elevated" data-testid={`gallery-uploaded-${g.id}`}>
            <img src={g.url} alt={g.title} className="w-full aspect-[4/3] object-cover" />
            <div className="p-2 text-xs">
              <div className="text-ink truncate">{g.title || "Untitled job"}</div>
              <div className="text-muted truncate">{[g.category, g.suburb].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            <button onClick={() => remove(g.id)} className="absolute top-2 right-2 w-8 h-8 bg-bg/80 text-ink hover:text-red-400 flex items-center justify-center" aria-label="Delete" data-testid={`gallery-delete-${g.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      {gallery.length > 0 && <p className="text-muted text-xs mt-4 inline-flex items-center gap-1"><Check size={12} className="text-gold" /> These show at the top of the Gallery page.</p>}
    </div>
  );
}
